import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  z: number;
}

interface ParticleSphereProps {
  particleCount?: number;
  rotationSeconds?: number;
  className?: string;
}

const buildFibonacciSphere = (count: number): Particle[] => {
  const points: Particle[] = [];
  const phi = Math.PI * (Math.sqrt(5) - 1);
  for (let i = 0; i < count; i += 1) {
    const y = 1 - (i / (count - 1)) * 2;
    const radius = Math.sqrt(1 - y * y);
    const theta = phi * i;
    points.push({
      x: Math.cos(theta) * radius,
      y,
      z: Math.sin(theta) * radius,
    });
  }
  return points;
};

export const ParticleSphere = ({
  particleCount = 520,
  rotationSeconds = 30,
  className,
}: ParticleSphereProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number>(0);
  const particlesRef = useRef<Particle[]>(buildFibonacciSphere(particleCount));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let cssWidth = 0;
    let cssHeight = 0;
    let radius = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      cssWidth = rect.width;
      cssHeight = rect.height;
      canvas.width = Math.floor(cssWidth * dpr);
      canvas.height = Math.floor(cssHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      radius = Math.min(cssWidth, cssHeight) * 0.42;
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);

    const particles = particlesRef.current;
    const start = performance.now();
    const angularSpeed = (Math.PI * 2) / (rotationSeconds * 1000);
    const tiltX = -0.35;

    const cosTiltX = Math.cos(tiltX);
    const sinTiltX = Math.sin(tiltX);

    const render = (now: number) => {
      const elapsed = now - start;
      const angleY = elapsed * angularSpeed;
      const cosY = Math.cos(angleY);
      const sinY = Math.sin(angleY);

      ctx.clearRect(0, 0, cssWidth, cssHeight);

      const centerX = cssWidth / 2;
      const centerY = cssHeight / 2;

      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];
        if (!p) continue;

        const x1 = p.x * cosY - p.z * sinY;
        const z1 = p.x * sinY + p.z * cosY;
        const y2 = p.y * cosTiltX - z1 * sinTiltX;
        const z2 = p.y * sinTiltX + z1 * cosTiltX;

        const depth = (z2 + 1) / 2;
        const screenX = centerX + x1 * radius;
        const screenY = centerY + y2 * radius;
        const size = 0.6 + depth * 1.8;
        const alpha = 0.18 + depth * 0.72;

        ctx.beginPath();
        ctx.arc(screenX, screenY, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(render);
    };

    animationRef.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animationRef.current);
      observer.disconnect();
    };
  }, [rotationSeconds]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
};
